import { useUpdate } from 'react-admin';
import { useCallback, useState } from 'react';
import {
  Box,
  CircularProgress,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import {
  useRecordContext,
  useRefresh,
  useGetIdentity,
  Labeled,
  UpdateButton,
} from 'react-admin';
import { useMutation } from '@tanstack/react-query';
import { adminRpcClient } from '@/providers/backendClient';
import { CollapsibleSection } from '@/components/CollapsibleSection';
import { FieldWithAction } from '@/components/FieldWithAction';

// Numeric field dialog component
const NumericFieldDialog = ({
  open,
  onClose,
  label,
  value,
  onSave,
  isLoading,
  helperText,
  inputProps,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  value: number | null;
  onSave: (value: number | null) => Promise<void>;
  isLoading: boolean;
  helperText?: string;
  inputProps?: any;
}) => {
  const [localValue, setLocalValue] = useState<string>(
    value === null ? '' : value.toString(),
  );

  const handleSave = useCallback(async () => {
    const numValue =
      localValue === '' || localValue === null
        ? null
        : Number(localValue);
    if (!isNaN(numValue as number) || numValue === null) {
      await onSave(numValue);
      onClose();
    }
  }, [localValue, onSave, onClose]);

  const handleClose = useCallback(() => {
    setLocalValue(value === null ? '' : value.toString());
    onClose();
  }, [value, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{label}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={label}
          type="number"
          value={localValue}
          onChange={e => setLocalValue(e.target.value)}
          helperText={helperText}
          inputProps={inputProps}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading && <CircularProgress size={16} />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AppConfigOverrides = () => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const { data: identity } = useGetIdentity();
  const [updateTherapist, { isLoading: isSaving }] = useUpdate(
    'therapists',
    { id: record.id },
  );

  // Dialog states for numeric fields
  const [numericDialog, setNumericDialog] = useState<{
    field: string;
    open: boolean;
  } | null>(null);

  // Individual field update handlers
  const updateField = useCallback(
    async (field: string, value: any) => {
      await updateTherapist(
        'therapists',
        {
          id: record.id,
          data: {
            [field]: value,
            lastUpdatedAt: new Date(),
            lastUpdatedBy: identity?.id,
          },
        },
        {
          onSuccess: () => {
            refresh();
          },
        },
      );
    },
    [updateTherapist, record.id, identity?.id, refresh],
  );

  // Numeric field handlers
  const openNumericDialog = useCallback((field: string) => {
    setNumericDialog({ field, open: true });
  }, []);

  const closeNumericDialog = useCallback(() => {
    setNumericDialog(null);
  }, []);

  const saveNumericField = useCallback(
    async (field: string, value: number | null) => {
      await updateField(field, value);
    },
    [updateField],
  );

  // Admin Config handler (special case - uses RPC)
  const {
    mutate: toggleHiddenTestAccount,
    isPending: isTogglingHidden,
  } = useMutation({
    mutationKey: [
      'PUBLIC_ADMIN_therapist_setIsHiddenTestAccount',
      record.id,
      record.isHiddenTestAccount,
    ],
    mutationFn: async () => {
      await adminRpcClient(
        'PUBLIC_ADMIN_therapist_setIsHiddenTestAccount',
        [record.id, !Boolean(record.isHiddenTestAccount)],
      );
    },
    onSettled: refresh,
  });

  return (
    <>
      <CollapsibleSection title="App Config Overrides">
        <Box display="flex" flexDirection="column" gap="1.5rem">
          {/* Debug Mode */}
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              marginBottom={1}
            >
              <Typography variant="h6">Debug Mode</Typography>
              <Tooltip title="Enable or disable debug menu access for this therapist">
                <IconButton size="small" sx={{ padding: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              gap={2}
              flexWrap="wrap"
            >
              <Labeled label="Debug Menu Enabled">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.isTestTherapist
                        ? 'Disable Debug Menu'
                        : 'Enable Debug Menu'
                    }
                    type="button"
                    color={
                      record.isTestTherapist ? 'error' : 'warning'
                    }
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.isTestTherapist
                        ? 'This will Disable Debug Menu access for this user'
                        : 'This will Enable Debug Menu access for this user'
                    }
                    icon={
                      record.isTestTherapist ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{ onSuccess: refresh }}
                    data={{
                      isTestTherapist: !record.isTestTherapist,
                      ...(record.isTestTherapist && {
                        advancedDebugMenuEnabled: false,
                      }),
                    }}
                  />
                </Box>
              </Labeled>
              {record.isTestTherapist && (
                <Labeled label="Advanced Debug Mode">
                  <Box sx={{ display: 'inline-block' }}>
                    <UpdateButton
                      resource="therapists"
                      label={
                        record.advancedDebugMenuEnabled
                          ? 'Disable Advanced Debug Mode'
                          : 'Enable Advanced Debug Mode'
                      }
                      type="button"
                      color={
                        record.advancedDebugMenuEnabled
                          ? 'error'
                          : 'warning'
                      }
                      variant="contained"
                      mutationMode="pessimistic"
                      confirmTitle="Are you sure?"
                      confirmContent={
                        record.advancedDebugMenuEnabled
                          ? 'This will Disable Advanced Debug Mode access for this user'
                          : 'This will Enable Advanced Debug Mode access for this user'
                      }
                      icon={
                        record.advancedDebugMenuEnabled ? (
                          <ClearIcon />
                        ) : (
                          <CheckIcon />
                        )
                      }
                      sx={{ color: '#fff', width: 'fit-content' }}
                      mutationOptions={{ onSuccess: refresh }}
                      data={{
                        advancedDebugMenuEnabled:
                          !record.advancedDebugMenuEnabled,
                      }}
                    />
                  </Box>
                </Labeled>
              )}
              <Labeled label="Modern Agent Prompts">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.ff_modernAgentPrompts
                        ? 'Disable Modern Agent Prompts'
                        : 'Enable Modern Agent Prompts'
                    }
                    type="button"
                    color={
                      record.ff_modernAgentPrompts
                        ? 'error'
                        : 'warning'
                    }
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.ff_modernAgentPrompts
                        ? 'This will disable the modern combined prompt structure for this user.'
                        : 'This will enable the modern combined prompt structure for this user.'
                    }
                    icon={
                      record.ff_modernAgentPrompts ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{
                      onSuccess: refresh,
                    }}
                    data={{
                      ff_modernAgentPrompts:
                        !record.ff_modernAgentPrompts,
                    }}
                  />
                </Box>
              </Labeled>
              <Labeled label="Insurance Company Filter (FTIC)">
                <Box sx={{ display: 'inline-block' }}>
                  <UpdateButton
                    resource="therapists"
                    label={
                      record.ff_insuranceCompanyFilter
                        ? 'Disable Insurance Note Check'
                        : 'Enable Insurance Note Check'
                    }
                    type="button"
                    color={
                      record.ff_insuranceCompanyFilter
                        ? 'error'
                        : 'warning'
                    }
                    variant="contained"
                    mutationMode="pessimistic"
                    confirmTitle="Are you sure?"
                    confirmContent={
                      record.ff_insuranceCompanyFilter
                        ? 'This will hide the Insurance Note Check feature for this therapist.'
                        : 'This will show the Insurance Note Check feature for this therapist (if enabled globally).'
                    }
                    icon={
                      record.ff_insuranceCompanyFilter ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )
                    }
                    sx={{ color: '#fff', width: 'fit-content' }}
                    mutationOptions={{
                      onSuccess: refresh,
                    }}
                    data={{
                      ff_insuranceCompanyFilter:
                        record.ff_insuranceCompanyFilter ? 0 : 1,
                    }}
                  />
                </Box>
              </Labeled>
            </Box>
          </Box>

          {/* OTA Updates */}
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              marginBottom={1}
            >
              <Typography variant="h6">OTA Updates</Typography>
              <Tooltip title="Force mandatory OTA updates for this therapist">
                <IconButton size="small" sx={{ padding: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Labeled label="Mandatory OTA Updates">
              <Box sx={{ display: 'inline-block' }}>
                <UpdateButton
                  resource="therapists"
                  label={
                    record.otaUpdatesMandatory
                      ? 'Disable Mandatory OTA Updates'
                      : 'Force OTA Updates'
                  }
                  type="button"
                  color={
                    record.otaUpdatesMandatory ? 'error' : 'warning'
                  }
                  variant="contained"
                  mutationMode="pessimistic"
                  confirmTitle="Are you sure?"
                  confirmContent={
                    record.otaUpdatesMandatory
                      ? 'This will Disable Mandatory OTA Updates for this therapist. This can be overridden by the global OTA settings.'
                      : 'This will Force the Therapist to update their app as soon as an OTA is available'
                  }
                  icon={
                    record.otaUpdatesMandatory ? (
                      <ClearIcon />
                    ) : (
                      <CheckIcon />
                    )
                  }
                  sx={{ color: '#fff', width: 'fit-content' }}
                  mutationOptions={{ onSuccess: refresh }}
                  data={{
                    otaUpdatesMandatory: !record.otaUpdatesMandatory,
                  }}
                />
              </Box>
            </Labeled>
          </Box>

          {/* Admin Config */}
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              marginBottom={1}
            >
              <Typography variant="h6">Admin Config</Typography>
              <Tooltip title="Add or remove this therapist from hidden test accounts">
                <IconButton size="small" sx={{ padding: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Labeled label="Hidden Test Account">
              <Box sx={{ display: 'inline-block' }}>
                <Button
                  color={
                    !record.isHiddenTestAccount ? 'warning' : 'error'
                  }
                  variant="contained"
                  sx={{ color: '#fff', width: 'fit-content' }}
                  size="small"
                  disabled={isTogglingHidden}
                  startIcon={
                    isTogglingHidden ? (
                      <CircularProgress size={18} />
                    ) : record.isHiddenTestAccount ? (
                      <ClearIcon />
                    ) : (
                      <CheckIcon />
                    )
                  }
                  onClick={() => toggleHiddenTestAccount()}
                >
                  {record.isHiddenTestAccount
                    ? 'Remove From Hidden Test Accounts'
                    : 'Add to Hidden Test Accounts'}
                </Button>
              </Box>
            </Labeled>
          </Box>

          {/* App Behavior Fields */}
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              marginBottom={1}
            >
              <Typography variant="h6">App Behavior</Typography>
              <Tooltip title="Override app behavior configuration values. Leave empty to use global config.">
                <IconButton size="small" sx={{ padding: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              gap={2}
              flexWrap="wrap"
            >
              <Labeled label="Free Trial Popup Placement">
                <FieldWithAction
                  value={
                    record.freeTrialPopupPlacement?.toString() || null
                  }
                  emptyText="Not set"
                  maxWidth="200px"
                  showActionOnHover={false}
                  action={
                    <Tooltip title="Number of remaining free trial notes when popup should appear. Leave empty to use global config. Use 0 or negative to disable.">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openNumericDialog('freeTrialPopupPlacement')
                        }
                        disabled={isSaving}
                        color="primary"
                        sx={{ padding: '4px' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                />
              </Labeled>
              <Labeled label="App Review Request Placement">
                <FieldWithAction
                  value={
                    record.appReviewRequestPlacement?.toString() ||
                    null
                  }
                  emptyText="Not set"
                  maxWidth="200px"
                  showActionOnHover={false}
                  action={
                    <Tooltip title="Number indicating when to request app review. Leave empty to use global config.">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openNumericDialog(
                            'appReviewRequestPlacement',
                          )
                        }
                        disabled={isSaving}
                        color="primary"
                        sx={{ padding: '4px' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                />
              </Labeled>
              <Labeled label="Global Tracing Sample Rate">
                <FieldWithAction
                  value={
                    record.tracing_sampleRate_global?.toString() ||
                    null
                  }
                  emptyText="Not set"
                  maxWidth="200px"
                  showActionOnHover={false}
                  action={
                    <Tooltip title="Global tracing sample rate (0.0 to 1.0). Leave empty to use global config.">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openNumericDialog(
                            'tracing_sampleRate_global',
                          )
                        }
                        disabled={isSaving}
                        color="primary"
                        sx={{ padding: '4px' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                />
              </Labeled>
              <Labeled label="Local Tracing Sample Rate">
                <FieldWithAction
                  value={
                    record.tracing_sampleRate_local?.toString() ||
                    null
                  }
                  emptyText="Not set"
                  maxWidth="200px"
                  showActionOnHover={false}
                  action={
                    <Tooltip title="Local tracing sample rate (0.0 to 1.0). Leave empty to use global config.">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openNumericDialog(
                            'tracing_sampleRate_local',
                          )
                        }
                        disabled={isSaving}
                        color="primary"
                        sx={{ padding: '4px' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                />
              </Labeled>
            </Box>
          </Box>
        </Box>
      </CollapsibleSection>

      {/* Numeric Field Dialogs */}
      {numericDialog && (
        <NumericFieldDialog
          open={numericDialog.open}
          onClose={closeNumericDialog}
          label={
            numericDialog.field === 'freeTrialPopupPlacement'
              ? 'Free Trial Popup Placement'
              : numericDialog.field === 'appReviewRequestPlacement'
                ? 'App Review Request Placement'
                : numericDialog.field === 'tracing_sampleRate_global'
                  ? 'Global Tracing Sample Rate'
                  : 'Local Tracing Sample Rate'
          }
          value={
            record[
              numericDialog.field as
                | 'freeTrialPopupPlacement'
                | 'appReviewRequestPlacement'
                | 'tracing_sampleRate_global'
                | 'tracing_sampleRate_local'
            ] ?? null
          }
          onSave={value =>
            saveNumericField(numericDialog.field, value)
          }
          isLoading={isSaving}
          helperText={
            numericDialog.field === 'freeTrialPopupPlacement'
              ? 'Number of remaining free trial notes when popup should appear. Leave empty to use global config. Use 0 or negative to disable.'
              : numericDialog.field === 'appReviewRequestPlacement'
                ? 'Number indicating when to request app review. Leave empty to use global config.'
                : numericDialog.field === 'tracing_sampleRate_global'
                  ? 'Global tracing sample rate (0.0 to 1.0). Leave empty to use global config.'
                  : 'Local tracing sample rate (0.0 to 1.0). Leave empty to use global config.'
          }
          inputProps={
            numericDialog.field.includes('tracing_sampleRate')
              ? { step: 0.01, min: 0, max: 1 }
              : { step: 1 }
          }
        />
      )}
    </>
  );
};
